import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { numberPerPageOptions } from "../../../assets/data/pagination/pagination";
import Button from "../Button/Button";
import Dropdown from "../Dropdown/Dropdown";
import ToolTip from "../ToolTip/ToolTip";
import './DataTable.css';

interface DataTableProps {
    title?: string,
    titleEle?: JSX.Element,
    thead?: JSX.Element,
    headings?: JSX.Element[],                   // Array of <th> or <tr>
    multiLineHeading?: boolean,                // Set to true if heading is an array of <tr>
    className?: string,
    tableContainerClassName?: string,
    children?: JSX.Element,
    // properties below are used for pagination
    totalPages?: number,                       // Calculated in parent component
    currentPage?: number,                      // Bind to the "current page state" in the parent component
    numberPerPage?: number,                    // Bind to the "number per page state" in the parent component
    changePage?: (e: number) => void           // Bind to the "current page state setter" in the parent component
    changeNumberPerPage?: (e: number) => void, // Bind to the "number per page state setter" in the parent componnet
    pageTiles?: number,                         // Determines the number of page tiles before and after current page
    errorPages?: number[]
    titleTooltip?: string
}

function DataTable(props: DataTableProps): ReactElement {

    const { headings, multiLineHeading, className, children, totalPages, currentPage, numberPerPage, changeNumberPerPage, changePage, pageTiles, errorPages } = props;

    const { t } = useTranslation("table");
    const displayPageButtons = () => {
        if (totalPages !== undefined && currentPage && changePage) {
            const tiles = (pageTiles === undefined || pageTiles < 3) ? 4 : pageTiles;
            const buttons = [];
            buttons.push(<Button
                key="pier-table-page-button-previous"
                onClick={() => changePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1 || totalPages === 0}
                type={(currentPage === 1 || totalPages === 0) ? "disabled" : "cancel"}
                testId="pier-table-previous-test"
                text={t("previous") as string}
                forceEnabled={true}
            />);
            if (totalPages === 0) {
                buttons.push(
                    <Button
                        key="pier-table-page-button-1"
                        disabled={true}
                        type="disabled"
                        text="1"
                        testId="pier-table-1-test"
                        ariaLabel={t("page") + " 1"}
                    />
                );
            }
            if (currentPage > totalPages && totalPages > 0) {
                changePage(1);
            }
            if (currentPage > (tiles + 1)) {
                const start = Math.max(2, currentPage - tiles - 1);
                const end = Math.max(currentPage - 1, totalPages - 1);
                const isEllipsisAfterError = errorPages?.some(p => p >= start && p <= end);
                buttons.push(
                    <Button
                        key="pier-table-page-button-1"
                        onClick={() => changePage(1)}
                        type="cancel"
                        testId="pier-table-1-test"
                        text="1"
                        forceEnabled={true}
                        className={errorPages?.includes(1) ? "table-error" : undefined}
                        ariaLabel={t("page") + " 1"}
                    />
                );
                buttons.push(
                    <Button
                        key="pier-table-page-button-front-pages"
                        className={"pier-table-page-button-font-back-pages"}
                        disabledClassName={isEllipsisAfterError ? "table-error" : undefined}
                        disabled={true}
                        type="cancel"
                        text="..."
                    />
                );
            }
            let frontTiles = tiles;
            let backTiles = tiles;
            if (currentPage > (tiles + 1)) {
                frontTiles -= 2;
            }
            if (currentPage < (totalPages - tiles)) {
                backTiles -= 2;
            }
            for (let i = Math.max(1, Math.min(currentPage, totalPages) - frontTiles); i <= Math.min(currentPage + backTiles, totalPages); i++) {
                const hasError = errorPages?.includes(i);
                const isCurrent = i === currentPage;
                buttons.push(
                    <Button
                        key={`pier-table-page-button-${i}`}
                        type={isCurrent ? "submit" : "cancel"}
                        disabled={isCurrent}
                        onClick={() => changePage(i)}
                        testId={`pier-table-${i}-test`}
                        text={(i).toString()}
                        forceEnabled={!isCurrent}
                        className={hasError ? "table-error" : undefined}
                        ariaLabel={t("page") + " " + i}
                    />
                );
            }
            if (currentPage < (totalPages - tiles)) {
                const start = Math.min(currentPage + tiles + 1, totalPages - 1);
                const end = totalPages - 1;
                const isEllipsisBeforeError = errorPages?.some(p => p >= start && p <= end);
                buttons.push(
                    <Button
                        key="pier-table-page-button-back-pages"
                        className="pier-table-page-button-font-back-pages"
                        disabledClassName={isEllipsisBeforeError ? "table-error" : undefined}
                        disabled={true}
                        type="cancel"
                        text="..."
                    />
                );
                buttons.push(
                    <Button
                        key={`pier-table-page-button-${totalPages}`}
                        type="cancel"
                        onClick={() => changePage(totalPages)}
                        testId={`pier-table-${totalPages}-test`}
                        text={totalPages.toString()}
                        forceEnabled={true}
                        className={errorPages?.includes(totalPages) ? "table-error" : undefined}
                        ariaLabel={t("page") + " " + totalPages}
                    />
                );
            }
            buttons.push(<Button
                key="pier-table-page-button-next"
                onClick={() => changePage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                type={(currentPage === totalPages || totalPages === 0) ? "disabled" : "cancel"}
                testId="pier-table-next-test"
                text={t("next") as string}
                forceEnabled={!(currentPage === totalPages || totalPages === 0)}
            />);
            return buttons;
        }
        return null;
    }

    return (
        <>
            <div className="pier-datatable-container">
                {props.title &&
                    <div className="pier-datatable-title-container">
                        <div title={props.titleTooltip}>{props.title}</div>
                    </div>
                }

                {props.titleEle &&
                    props.titleEle
                }
                <div className={props.tableContainerClassName}>
                    <table className={"pier-datatable" + (className ? " " + className : "")} role="presentation">
                        {
                            props.thead ?
                                <>{props.thead}</>
                                :
                                <thead>
                                    {multiLineHeading && headings ?
                                        headings.map((heading) => {
                                            return (heading)
                                        })
                                        :
                                        <tr>
                                            {headings && headings.map((heading) => {
                                                const { "data-tip": tip, children, ...restProps } = heading.props;
                                                if (tip) {
                                                    return <th key={heading.key} {...restProps}>
                                                        <ToolTip contents={children}>
                                                            {tip}
                                                        </ToolTip>
                                                    </th>
                                                } else {
                                                    return (heading)
                                                }
                                            })
                                            }
                                        </tr>
                                    }
                                </thead>
                        }
                        <tbody>
                            {children}
                        </tbody>
                    </table>
                </div>
                {(totalPages !== undefined && currentPage && changePage) && <div className="pier-datatable-pagination">
                    <div>
                        {displayPageButtons()}
                    </div>
                    {(numberPerPage && changeNumberPerPage) &&
                        <Dropdown
                            forceEnabled={true}
                            options={numberPerPageOptions}
                            value={numberPerPage.toString()}
                            onChange={(e) => changeNumberPerPage(parseInt((e.target as HTMLSelectElement).value))}
                            ariaLabel={t("per-page") as string}
                            testid="per-page-dropdown"
                            name="per-page-dropdown" />
                    }
                </div>}
            </div>
        </>
    );
}

export default DataTable;